/**
 * SOLOMON — ELEVENLABS TTS HOOK
 * Phase 1 / Day 6: Voice Output
 *
 * Sends Solomon's text responses to the backend /api/voice/speak endpoint,
 * streams the audio, and plays it via the Web Audio API (AudioContext).
 *
 * Uses AudioContext instead of Audio element for Safari compatibility —
 * Safari blocks Audio.play() when called asynchronously after an API call,
 * but AudioContext stays unlocked after the first user gesture.
 *
 * Long-response handling: responses up to MAX_SPEAK_CHARS go in a single
 * request; longer ones are split at sentence boundaries and played
 * sequentially so the entire response is spoken without truncation.
 */

import { useState, useRef, useCallback } from 'react';
import { latexToPlainText } from '../utils/latexNormalize';
import { expandSymbolsForSpeech } from './speechSymbols';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';
// Max chars per single TTS request. Backend caps at 5000 — stay below that.
const MAX_SPEAK_CHARS = 4500;
// Hard ceiling for total speech across all chunks. Anything longer plays
// the first SPEECH_HARD_CAP chars and a friendly trailing notice.
const SPEECH_HARD_CAP = 30_000;

// ============================================================
// TEXT PROCESSING
// ============================================================

/**
 * Convert numbers and currency shorthand into words for natural TTS pronunciation.
 * Handles: $5M, 1,000, 3.5%, $100K, 10B, etc.
 */
function expandNumbersForSpeech(text) {
  const magnitudes = {
    k: 'thousand', K: 'thousand',
    m: 'million',  M: 'million',
    b: 'billion',  B: 'billion',
    t: 'trillion', T: 'trillion',
  };

  // $5M → "5 million dollars", $100K → "100 thousand dollars"
  text = text.replace(/\$\s*([\d,.]+)\s*([kmbtKMBT])\b/g, (_, num, mag) => {
    return `${num.replace(/,/g, '')} ${magnitudes[mag]} dollars`;
  });

  // 5M, 100K, 2.5B (without $) → "5 million", etc.
  text = text.replace(/\b([\d,.]+)\s*([kmbtKMBT])\b/g, (_, num, mag) => {
    return `${num.replace(/,/g, '')} ${magnitudes[mag]}`;
  });

  // $1,000 or $250,000 → spoken naturally (just remove $ sign, commas handled below)
  text = text.replace(/\$\s*([\d,]+(?:\.\d+)?)/g, (_, num) => {
    return `${num} dollars`;
  });

  // Simple integer ranges with commas: 1,000 → 1000 (TTS handles plain numbers well)
  text = text.replace(/\b(\d{1,3}(?:,\d{3})+)\b/g, (match) => {
    return match.replace(/,/g, '');
  });

  // Percentages: ensure "percent" is spoken
  text = text.replace(/([\d.]+)\s*%/g, '$1 percent');

  return text;
}

/**
 * Convert math symbols and simple expressions to spoken words.
 * Complex equations (LaTeX, multi-line) are replaced with "equation shown in text".
 */
function expandMathForSpeech(text) {
  // Skip complex LaTeX-style math blocks entirely
  text = text.replace(/\$\$[\s\S]*?\$\$/g, ' equation shown in text ');
  text = text.replace(/\\\[[\s\S]*?\\\]/g, ' equation shown in text ');
  text = text.replace(/\\frac\{[^}]*\}\{[^}]*\}/g, ' fraction shown in text ');

  // Remaining LaTeX — single-$ command spans ($\approx$), \(...\) inline, and
  // bare commands (\times, \mathbf{}) — to plain symbols, so the operator
  // wording below speaks them. Issue 18's fix covered only the block shapes
  // above; without this, these forms reached ElevenLabs as raw backslash text.
  text = latexToPlainText(text);

  // Simple operator replacements (with word boundaries or spacing)
  text = text.replace(/(\d)\s*\+\s*(\d)/g, '$1 plus $2');
  text = text.replace(/(\d)\s*-\s*(\d)/g, '$1 minus $2');
  text = text.replace(/(\d)\s*[×x]\s*(\d)/gi, '$1 times $2');
  text = text.replace(/(\d)\s*[÷]\s*(\d)/g, '$1 divided by $2');
  text = text.replace(/(\d)\s*\/\s*(\d)/g, '$1 divided by $2');
  text = text.replace(/(\d)\s*=\s*(\d)/g, '$1 equals $2');
  text = text.replace(/(\d)\s*\^\s*(\d)/g, '$1 to the power of $2');

  // Standalone symbols (not between digits). √ keeps its own rule because it
  // swallows the operand ("√52" → "square root of 52"), which a flat glyph
  // table cannot express.
  text = text.replace(/√\s*(\d+)/g, 'square root of $1');

  // Every other glyph — including the × / ÷ the digit-pair rules above didn't
  // catch (a paren between operator and digit: "(… 0.05) ÷ 12") — goes through
  // the shared table, which is parity-tested against what latexNormalize can
  // emit. Runs before the ASCII > / < rules, preserving the prior ordering.
  text = expandSymbolsForSpeech(text);

  text = text.replace(/>/g, ' greater than ');
  text = text.replace(/</g, ' less than ');

  return text;
}

/**
 * Strip markdown / code / link syntax and special characters so the spoken
 * text sounds natural. Also expands numbers and math for better TTS pronunciation.
 * Does NOT truncate — see splitForSpeech for length handling.
 */
function cleanTextForSpeech(content) {
  if (!content) return '';

  let text = content
    .replace(/^\s*`?\[image:[0-9a-f-]{36}\]`?\s*$/gim, ' ')  // generated-image tokens — never read UUIDs aloud
    .replace(/```[\s\S]*?```/g, ' ')                  // strip fenced code blocks entirely
    .replace(/\*\*([^*]+)\*\*/g, '$1')                // bold → plain
    .replace(/\*([^*]+)\*/g,   '$1')                  // italic → plain
    .replace(/`([^`]+)`/g,     '$1')                   // inline code → plain
    .replace(/#{1,6}\s/g,      '')                     // heading markers
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')           // links → link text
    .replace(/^[\s]*[-•●▪▸►]\s*/gm, '')               // bullet points
    .replace(/^[\s]*\d+\.\s+/gm, '')                   // numbered list markers
    .replace(/---+|===+|\*\*\*+/g, ' ')                // horizontal rules
    .replace(/[—–]/g, ', ')                             // em/en dashes → pause
    .replace(/[~_|>]/g, ' ')                            // misc markdown chars
    .replace(/\n{2,}/g,        '. ')                    // paragraph breaks → sentence break
    .replace(/\n/g,            ' ')                     // remaining newlines
    .replace(/\s+/g,           ' ')                     // collapse whitespace
    .trim();

  // Expand numbers, currency, and math for natural pronunciation
  text = expandNumbersForSpeech(text);
  text = expandMathForSpeech(text);

  return text;
}

/**
 * Split cleaned speech text into chunks ≤ maxChars, preferring sentence
 * boundaries (`. ` / `? ` / `! `), then word boundaries, then a hard cut.
 */
function splitForSpeech(text, maxChars = MAX_SPEAK_CHARS) {
  if (!text) return [];
  if (text.length <= maxChars) return [text];

  const chunks = [];
  let remaining = text;

  while (remaining.length > 0) {
    if (remaining.length <= maxChars) {
      chunks.push(remaining);
      break;
    }

    const slice = remaining.slice(0, maxChars);
    const minBreak = Math.floor(maxChars * 0.5);

    const sentenceEnd = Math.max(
      slice.lastIndexOf('. '),
      slice.lastIndexOf('? '),
      slice.lastIndexOf('! ')
    );
    const wordBreak = slice.lastIndexOf(' ');

    let cutAt;
    if (sentenceEnd > minBreak)      cutAt = sentenceEnd + 2;   // include ". "
    else if (wordBreak > minBreak)   cutAt = wordBreak + 1;
    else                             cutAt = maxChars;          // hard fallback

    chunks.push(remaining.slice(0, cutAt).trim());
    remaining = remaining.slice(cutAt).trim();
  }

  return chunks.filter((c) => c.length > 0);
}

// Back-compat export — old callers used prepareTextForSpeech for the
// cleaned single-string form. Returns the first chunk (most callers don't
// need the full split list).
function prepareTextForSpeech(content) {
  const chunks = splitForSpeech(cleanTextForSpeech(content));
  return chunks[0] || '';
}

// ============================================================
// HOOK
// ============================================================

export function useElevenLabs({ onSpeakStart, onSpeakEnd, onError, authFetch } = {}) {
  // Fall back to plain fetch if authFetch not provided
  const apiFetch = authFetch || ((...args) => fetch(...args));
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isLoading,  setIsLoading]  = useState(false);

  // AudioContext — persists across speaks, stays unlocked after first user gesture
  const audioCtxRef    = useRef(null);
  const sourceNodeRef  = useRef(null);
  // Per-playback ID — bumped by cleanupAudio so any in-flight fetch/decode
  // started before the cleanup can detect it's stale and bail out before
  // ever starting its source node. Eliminates audio overlap when a new
  // message arrives mid-fetch and lets the voice toggle stop pending audio.
  const playbackIdRef  = useRef(0);
  // AbortController for the in-flight /api/voice/speak request
  const abortCtrlRef   = useRef(null);

  /**
   * Get (or create) the AudioContext, ensuring it's running.
   * Must be called from within a user gesture the FIRST time.
   * After that, it stays open and can be used asynchronously.
   * Returns a Promise so callers can await resume completion.
   */
  const getAudioContext = useCallback(async () => {
    if (!audioCtxRef.current) {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return null;
      audioCtxRef.current = new AudioCtx();
    }
    // Safari suspends AudioContext by default, and browsers suspend it after
    // tab backgrounding or inactivity — MUST await resume before playing audio
    if (audioCtxRef.current.state === 'suspended') {
      try {
        await audioCtxRef.current.resume();
      } catch (err) {
        console.warn('[Solomon/TTS] AudioContext resume failed:', err.message);
        return null;
      }
    }
    return audioCtxRef.current;
  }, []);

  /**
   * Call this from a user click (e.g. the speaker button toggle).
   * Creates and unlocks the AudioContext so future async plays work in Safari.
   */
  const unlockAudio = useCallback(() => {
    // Fire-and-forget — this runs on a user gesture so resume will succeed
    getAudioContext();
  }, [getAudioContext]);

  /**
   * Stop any currently playing audio AND invalidate any in-flight playback
   * pipeline (fetch, decode) so it can't start a source after we've cleaned
   * up. Single source of truth for "make audio stop".
   */
  const cleanupAudio = useCallback(() => {
    // Bump the generation so any in-flight speakText() bails before starting
    playbackIdRef.current += 1;

    // Abort the in-flight TTS fetch (if any)
    if (abortCtrlRef.current) {
      try { abortCtrlRef.current.abort(); } catch (e) { console.warn('[Solomon/Voice] Abort cleanup:', e.message); }
      abortCtrlRef.current = null;
    }

    // Stop and disconnect any currently playing source node
    if (sourceNodeRef.current) {
      try { sourceNodeRef.current.onended = null; } catch (e) { console.warn('[Solomon/Voice] Cleanup onended:', e.message); }
      try { sourceNodeRef.current.stop(); }        catch (e) { console.warn('[Solomon/Voice] Cleanup stop:', e.message); }
      try { sourceNodeRef.current.disconnect(); }  catch (e) { console.warn('[Solomon/Voice] Cleanup disconnect:', e.message); }
      sourceNodeRef.current = null;
    }
  }, []);

  /**
   * Stop speaking immediately
   */
  const stopSpeaking = useCallback(() => {
    cleanupAudio();
    setIsSpeaking(false);
    setIsLoading(false);
    onSpeakEnd?.();
  }, [cleanupAudio, onSpeakEnd]);

  /**
   * Fetch + decode + play one chunk of speech. Returns a Promise that
   * resolves when the source's onended fires (or rejects on error / abort).
   * Caller is responsible for stale-check before and after.
   */
  const playChunk = useCallback(async (chunk, voiceId, isStale, ctrl) => {
    const response = await apiFetch(`${API_BASE}/api/voice/speak`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ text: chunk, voiceId }),
      signal:  ctrl.signal,
    });

    if (isStale()) return;

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      const e = new Error(err.error || `TTS failed: ${response.status}`);
      e.status = response.status;
      throw e;
    }

    const audioBlob   = await response.blob();
    if (isStale()) return;
    const arrayBuffer = await audioBlob.arrayBuffer();
    if (isStale()) return;

    const ctx = await getAudioContext();
    if (isStale()) return;
    if (!ctx) throw new Error('AudioContext not available — tap the speaker button to enable audio');

    if (ctx.state !== 'running') {
      console.warn('[Solomon/TTS] AudioContext state:', ctx.state, '— attempting resume');
      await ctx.resume();
      if (isStale()) return;
    }

    const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
    if (isStale()) return;

    const source = ctx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(ctx.destination);
    sourceNodeRef.current = source;

    // Wait for this chunk to finish playing before resolving
    return new Promise((resolve) => {
      source.onended = () => {
        if (sourceNodeRef.current === source) sourceNodeRef.current = null;
        resolve();
      };
      source.start(0);
    });
  }, [apiFetch, getAudioContext]);

  /**
   * Convert text to speech and play via AudioContext. Long responses are
   * split into chunks and played sequentially.
   *
   * Cleans up any prior playback first, then aborts itself if a newer
   * speakText() (or stopSpeaking()) bumps playbackIdRef while it's mid-async.
   */
  const speakText = useCallback(async (text, voiceId) => {
    if (!text || !voiceId) return;

    // Cancel any in-flight or playing audio (bumps playbackIdRef)
    cleanupAudio();
    setIsSpeaking(false);

    const cleaned = cleanTextForSpeech(text);
    if (!cleaned) return;

    // Hard ceiling — anything past this gets dropped with a friendly notice.
    let speechBody = cleaned;
    let truncatedNotice = false;
    if (speechBody.length > SPEECH_HARD_CAP) {
      speechBody = speechBody.slice(0, SPEECH_HARD_CAP);
      truncatedNotice = true;
    }

    const chunks = splitForSpeech(speechBody, MAX_SPEAK_CHARS);
    if (chunks.length === 0) return;

    // Capture this call's generation. If anything bumps the ref before we
    // reach source.start() on the FINAL chunk, the pipeline aborts silently.
    const myId = playbackIdRef.current;
    const isStale = () => playbackIdRef.current !== myId;

    // Fresh AbortController for this call's fetches (shared across chunks)
    const ctrl = new AbortController();
    abortCtrlRef.current = ctrl;

    try {
      setIsLoading(true);
      let started = false;

      for (let i = 0; i < chunks.length; i++) {
        if (isStale()) return;

        const promise = playChunk(chunks[i], voiceId, isStale, ctrl);

        // Mark as speaking the moment the FIRST chunk's source starts
        if (!started) {
          // Note: playChunk awaits decode before resolving, so by the time
          // it returns the source is playing. Flip state when the promise
          // exists so the orb / isSpeaking flag activates immediately.
          setIsLoading(false);
          setIsSpeaking(true);
          onSpeakStart?.();
          started = true;
        }

        await promise;
        if (isStale()) return;
      }

      // All chunks played successfully
      setIsSpeaking(false);
      onSpeakEnd?.();

      if (truncatedNotice) {
        onError?.('Response was very long — voice played the first portion. Full text shown above.');
      }

      if (abortCtrlRef.current === ctrl) abortCtrlRef.current = null;

    } catch (err) {
      // AbortError from cleanupAudio is expected — don't surface it
      if (err.name === 'AbortError' || isStale()) return;

      console.error('[Solomon/TTS] Error:', err.message);
      setIsSpeaking(false);
      setIsLoading(false);
      cleanupAudio();

      // Friendlier message when ElevenLabs rejected for length
      const friendly = (err.status === 400 || err.status === 413 || /too long/i.test(err.message))
        ? 'Response too long for voice — displayed as text instead.'
        : `Voice unavailable: ${err.message}`;
      onError?.(friendly);
    }
  }, [cleanupAudio, playChunk, onSpeakStart, onSpeakEnd, onError]);

  /**
   * Fetch list of available voices
   */
  const fetchVoices = useCallback(async () => {
    try {
      const response = await apiFetch(`${API_BASE}/api/voice/voices`);
      if (!response.ok) throw new Error(`Voices fetch failed: ${response.status}`);
      const data = await response.json();
      return data.voices || [];
    } catch (err) {
      console.error('[Solomon/TTS] Voices error:', err.message);
      return [];
    }
  }, [apiFetch]);

  return {
    isSpeaking,
    isLoading,
    speakText,
    stopSpeaking,
    fetchVoices,
    unlockAudio,           // call this on speaker button click to unlock Safari
    prepareTextForSpeech,
  };
}
